import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Stack, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, CircularProgress, Alert, Divider, IconButton, Tooltip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { CLP, FINANCE_COLORS, fmtFecha } from '../../utils/finance';
import { useBranch } from '../../context/BranchContext';
import { generateCierreCajaPDF } from '../../services/cierreCajaPDF';
import DetalleCierreDialog from '../../components/caja/DetalleCierreDialog';

const { PRIMARY, TEAL, AMBER, DANGER } = FINANCE_COLORS;
const PIE_COLORS = ['#1A3A5C', '#2CB8A0', '#F59E0B', '#6366F1', '#DC2626', '#10B981', '#8B5CF6'];

const mesActual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export default function InformeFinanciero() {
  const { activeBranchId, activeBranch, isAdmin, isAllBranches } = useBranch();
  const [mes, setMes] = useState(mesActual());
  const [cierreDetalle, setCierreDetalle] = useState(null);
  const [ventas, setVentas] = useState([]);
  const [ventasPrev, setVentasPrev] = useState([]);
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [y, m] = mes.split('-').map(Number);
      const from = new Date(y, m - 1, 1, 0, 0, 0);
      const to = new Date(y, m, 0, 23, 59, 59);
      const prevFrom = new Date(y, m - 2, 1, 0, 0, 0);
      const prevTo = new Date(y, m - 1, 0, 23, 59, 59);

      const fetch = async (f, t) => {
        const snap = await getDocs(query(
          collection(db, 'Sales'),
          where('createdAt', '>=', Timestamp.fromDate(f)),
          where('createdAt', '<=', Timestamp.fromDate(t)),
        ));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((v) => v.estado !== 'anulada');
      };
      const [cur, prev, cSnap] = await Promise.all([
        fetch(from, to), fetch(prevFrom, prevTo), getDocs(collection(db, 'turnosCaja')),
      ]);
      const filterByBranch = (arr) => arr.filter((v) => isAdmin || isAllBranches || v.branchId === activeBranchId);
      setVentas(filterByBranch(cur)); setVentasPrev(filterByBranch(prev));
      setCierres(cSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.estado !== 'abierto' && (t.fechaTurno || '').startsWith(mes) && (isAdmin || isAllBranches || t.branchId === activeBranchId))
        .sort((a, b) => (b.fechaTurno || '').localeCompare(a.fechaTurno || '')));
    } catch (e) {
      setError('Error al cargar: ' + e.message + (e.code === 'failed-precondition' ? ' (puede requerir índice)' : ''));
    } finally {
      setLoading(false);
    }
  }, [mes, activeBranchId, isAdmin, isAllBranches]);

  useEffect(() => { cargar(); }, [cargar]);

  const val = (v) => v.totalFinal || v.total || 0;
  const netoV = (v) => v.subtotalNeto || v.subtotal || 0;

  const kpis = useMemo(() => {
    const total = ventas.reduce((s, v) => s + val(v), 0);
    const totalPrev = ventasPrev.reduce((s, v) => s + val(v), 0);
    const count = ventas.length;
    const ticket = count ? Math.round(total / count) : 0;
    const growth = totalPrev ? Math.round(((total - totalPrev) / totalPrev) * 100) : null;
    return { total, count, ticket, growth };
  }, [ventas, ventasPrev]);

  const daily = useMemo(() => {
    const map = {};
    ventas.forEach((v) => {
      const d = v.createdAt?.toDate ? v.createdAt.toDate() : new Date(v.createdAt);
      if (isNaN(d)) return;
      const key = String(d.getDate()).padStart(2, '0');
      map[key] = (map[key] || 0) + netoV(v);
    });
    return Object.entries(map).map(([dia, neto]) => ({ dia, neto })).sort((a, b) => a.dia.localeCompare(b.dia));
  }, [ventas]);

  const porUnidad = useMemo(() => {
    const map = {};
    ventas.forEach((v) => (v.items || []).forEach((it) => {
      const k = it.unidadNegocio || it.tipoPrestacion || 'Otros';
      map[k] = (map[k] || 0) + (it.total || 0);
    }));
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [ventas]);

  const top10 = useMemo(() => {
    const map = {};
    ventas.forEach((v) => (v.items || []).forEach((it) => {
      const k = it.descripcion || 'Otro';
      map[k] = (map[k] || 0) + (it.total || 0);
    }));
    return Object.entries(map).map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value).slice(0, 10);
  }, [ventas]);

  const medios = useMemo(() => {
    // por semana del mes
    const map = {};
    ventas.forEach((v) => {
      const d = v.createdAt?.toDate ? v.createdAt.toDate() : new Date(v.createdAt);
      if (isNaN(d)) return;
      const semana = `S${Math.ceil(d.getDate() / 7)}`;
      map[semana] = map[semana] || { semana, efectivo: 0, debito: 0, credito: 0, transferencia: 0 };
      (v.pagos || [{ medio: v.paymentMethod, monto: val(v) }]).forEach((p) => {
        if (['efectivo', 'debito', 'credito', 'transferencia'].includes(p.medio)) {
          map[semana][p.medio] += p.monto || 0;
        }
      });
    });
    return Object.values(map).sort((a, b) => a.semana.localeCompare(b.semana));
  }, [ventas]);

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800} color={PRIMARY}>Informe Financiero</Typography>
          <Typography variant="body2" color="text.secondary">KPIs y reportes de gestión del período.</Typography>
        </Box>
        <TextField size="small" type="month" label="Mes" value={mes} onChange={(e) => setMes(e.target.value)}
          InputLabelProps={{ shrink: true }} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Kpi label="Ventas totales" value={CLP(kpis.total)} color={PRIMARY} />
            <Kpi label="Transacciones" value={kpis.count} color={TEAL} />
            <Kpi label="Ticket promedio" value={CLP(kpis.ticket)} color={AMBER} />
            <Kpi label="Crecimiento vs mes anterior"
              value={kpis.growth === null ? '–' : `${kpis.growth > 0 ? '+' : ''}${kpis.growth}%`}
              color={kpis.growth === null ? PRIMARY : kpis.growth >= 0 ? '#10B981' : DANGER} />
          </Grid>

          <Grid container spacing={2}>
            {/* Ventas diarias */}
            <Grid size={{ xs: 12, md: 8 }}>
              <ChartCard title="Ventas netas por día">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" /><YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <RTooltip formatter={(v) => CLP(v)} />
                    <Line type="monotone" dataKey="neto" stroke={PRIMARY} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            {/* Pie unidad */}
            <Grid size={{ xs: 12, md: 4 }}>
              <ChartCard title="Distribución por unidad de negocio">
                {porUnidad.length === 0 ? <Empty /> : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={porUnidad} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                        {porUnidad.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => CLP(v)} /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
            {/* Top 10 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Top 10 prestaciones">
                {top10.length === 0 ? <Empty /> : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={top10} layout="vertical" margin={{ left: 20 }}>
                      <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                      <RTooltip formatter={(v) => CLP(v)} />
                      <Bar dataKey="value" fill={TEAL} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
            {/* Medios por semana */}
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Medios de pago por semana">
                {medios.length === 0 ? <Empty /> : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={medios}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="semana" /><YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <RTooltip formatter={(v) => CLP(v)} /><Legend />
                      <Bar dataKey="efectivo" fill="#10B981" /><Bar dataKey="debito" fill="#1A3A5C" />
                      <Bar dataKey="credito" fill="#F59E0B" /><Bar dataKey="transferencia" fill="#6366F1" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
            {/* Cierres de caja */}
            <Grid size={{ xs: 12 }}>
              <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2 }}>Cierres de caja del período</Typography>
                <Divider />
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                      <TableCell>Fecha</TableCell><TableCell>Caja</TableCell><TableCell>Cajero</TableCell>
                      <TableCell align="right">Total ventas</TableCell><TableCell align="right">Diferencia</TableCell>
                      <TableCell>Estado</TableCell><TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cierres.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin cierres en el período.</TableCell></TableRow>
                    ) : cierres.map((c) => {
                      const alerta = Math.abs(c.diferencia || 0) > 5000;
                      return (
                        <TableRow key={c.id} hover sx={{ bgcolor: alerta ? 'rgba(220,38,38,0.05)' : 'transparent' }}>
                          <TableCell>{c.fechaTurno}</TableCell>
                          <TableCell>{c.cajaNombre}</TableCell>
                          <TableCell>{c.usuarioCierreNombre || c.usuarioAperturaNombre}</TableCell>
                          <TableCell align="right">{CLP(c.totalBruto)}</TableCell>
                          <TableCell align="right" sx={{ color: alerta ? DANGER : 'inherit', fontWeight: alerta ? 700 : 400 }}>
                            {CLP(c.diferencia || 0)}
                          </TableCell>
                          <TableCell><Chip size="small" label={c.estado} color={c.estado === 'cuadrado' ? 'success' : 'warning'} /></TableCell>
                          <TableCell align="center">
                            <Tooltip title="Reimprimir cierre de caja">
                              <IconButton size="small"
                                onClick={() => generateCierreCajaPDF(c, { branch: activeBranch || {}, reimpresion: true })}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ver detalle del cierre">
                              <IconButton size="small" onClick={() => setCierreDetalle(c)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <DetalleCierreDialog open={!!cierreDetalle} onClose={() => setCierreDetalle(null)}
        turno={cierreDetalle} branch={activeBranch} />
    </Box>
  );
}

function Kpi({ label, value, color }) {
  return (
    <Grid size={{ xs: 6, md: 3 }}>
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
      </Paper>
    </Grid>
  );
}
function ChartCard({ title, children }) {
  return (
    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
      {children}
    </Paper>
  );
}
function Empty() {
  return <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>Sin datos.</Typography>;
}
